import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Form, Modal, Dropdown, Icon } from 'semantic-ui-react';
import { Input } from '../../../lib/custom-ui';

import AttachmentAddPopup from './AttachmentAddPopup';
import Attachments from '../Attachments';

import { useForm } from '../../../hooks';

import styles from './ModelEditStep.module.scss';

const ModelEditStep = React.memo(
  ({
    attachments,
    protocols,
    defaultValue,
    onUpdate,
    onClose,
    onAttachmentCreate,
    onAttachmentUpdate,
    onAttachmentDelete,
  }) => {
    const [t] = useTranslation();
    const [data, handleFieldChange] = useForm(() => ({
      name: '',
      brand: '',
      ...defaultValue,
      protocolId: defaultValue.protocolId?.id || defaultValue.protocolId,
    }));

    const protocolsOptions = [];

    protocols.forEach((item) => {
      protocolsOptions.push({ key: item.id, value: item.id, text: item.name });
    });

    const nameField = useRef(null);

    const [nameError, setNameError] = useState(false);
    const [brandError, setBrandError] = useState(false);
    const [protocolIdError, setProtocolIdError] = useState(false);

    const handleSubmit = useCallback(() => {
      const cleanData = {
        ...data,
        name: data.name.trim(),
        brand: data.brand.trim(),
      };

      let error;
      if (!cleanData.name) {
        setNameError(true);
        error = true;
      }
      if (!cleanData.brand) {
        setBrandError(true);
        error = true;
      }
      if (!cleanData.protocolId) {
        setProtocolIdError(true);
        error = true;
      }
      if (error) return;

      if (cleanData !== defaultValue) {
        onUpdate(cleanData);
      }

      onClose();
    }, [data, defaultValue, onClose, onUpdate]);

    useEffect(() => {
      nameField.current.select();
    }, []);

    const handleCoverUpdate = useCallback(
      (newCoverAttachmentId) => {
        onUpdate({
          id: data.id,
          coverAttachmentId: newCoverAttachmentId,
        });
      },
      [data.id, onUpdate],
    );

    const handleAttachmentCreate = useCallback(
      (newData) => {
        onAttachmentCreate(defaultValue.id, newData);
      },
      [defaultValue.id, onAttachmentCreate],
    );

    return (
      <>
        <Modal.Header>
          {t('common.editDeviceModel', {
            context: 'title',
          })}
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field required error={nameError} onFocus={() => setNameError(false)}>
              <label htmlFor="name">{t('common.enterModelTitle')}</label>
              <Input
                fluid
                ref={nameField}
                name="name"
                value={data.name}
                className={styles.field}
                onChange={handleFieldChange}
              />
            </Form.Field>
            <Form.Field required error={brandError} onFocus={() => setBrandError(false)}>
              <label htmlFor="brand">{t('common.selectModelBrand')}</label>
              <Input
                fluid
                name="brand"
                value={data.brand}
                className={styles.field}
                onChange={handleFieldChange}
              />
            </Form.Field>
            <Form.Field required error={protocolIdError} onFocus={() => setProtocolIdError(false)}>
              <label htmlFor="protocolId">{t('common.selectModelProtocol')}</label>
              <Dropdown
                name="protocolId"
                className={styles.field}
                placeholder={t('common.selectModelProtocol')}
                search
                fluid
                selection
                value={data.protocolId}
                options={protocolsOptions}
                onChange={handleFieldChange}
              />
            </Form.Field>
            <div className={styles.contentModule}>
              <div className={styles.moduleWrapper}>
                <Icon name="file image" className={styles.moduleIcon} />
                <AttachmentAddPopup onCreate={handleAttachmentCreate}>
                  <Button
                    floated="right"
                    className={classNames(styles.actionButton, styles.attachmentButton)}
                  >
                    <Icon name="attach" className={styles.actionIcon} />
                    {t('common.attachment')}
                  </Button>
                </AttachmentAddPopup>
                <div className={styles.moduleHeader}>{t('common.attachments')}</div>
                {attachments.length > 0 && (
                  <Attachments
                    items={attachments}
                    onUpdate={onAttachmentUpdate}
                    onDelete={onAttachmentDelete}
                    onCoverUpdate={handleCoverUpdate}
                  />
                )}
              </div>
            </div>
            <Button
              color="green"
              icon="checkmark"
              content={t('action.save')}
              floated="right"
              onClick={handleSubmit}
            />
            <div className="clearBoth" />
          </Form>
        </Modal.Content>
      </>
    );
  },
);

ModelEditStep.propTypes = {
  attachments: PropTypes.arrayOf(PropTypes.any).isRequired,
  protocols: PropTypes.arrayOf(PropTypes.any).isRequired,
  defaultValue: PropTypes.objectOf(PropTypes.any).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onAttachmentCreate: PropTypes.func.isRequired,
  onAttachmentUpdate: PropTypes.func.isRequired,
  onAttachmentDelete: PropTypes.func.isRequired,
};

export default ModelEditStep;
